import { useQuery } from '@tanstack/react-query'
import { AgentState } from 'v2/pages/flow/types'
import { settingsService } from 'v2/shared/services/settings-service'
import { getAPIConsoleQueryKeys, getSettingsQueryKeys } from 'v2/shared/utils/query-key-management'
import {
  CONNECT_PAGE_SIZE,
  collectPages,
  hasNextFromMeta,
  hasNextFromTotal,
  type PagedOptions,
} from './sandbox-connect-paging'
import { SandboxConnectKind } from './sandbox-setup-copy'

export type { SandboxConnectOption } from './sandbox-connect-paging'

export interface SandboxConnectOptions extends PagedOptions {
  isPending: boolean
  isError: boolean
}

const EMPTY: PagedOptions = { options: [], truncated: false }

/**
 * The pickable items for one Connect dialog, across every page.
 *
 * Three separate queries rather than a switch inside one `queryFn`, so each sits
 * under the prefix the rest of the app already invalidates - creating an
 * application from the dialog's own side sheet refreshes this list through the
 * same key. The `sandbox-connect` suffix is deliberate: this caches an
 * aggregated `{options, truncated}` shape, which is not what the other consumers
 * of those base keys hold.
 *
 * Only the open kind is enabled: a dialog for one kind must not fire three
 * requests, and two of the three endpoints 403 for a calls-only role.
 */
export const useSandboxConnectOptions = (kind?: SandboxConnectKind): SandboxConnectOptions => {
  // Agents page by `page` against a `total`, and are filtered to ACTIVE
  // server-side: a draft or paused agent cannot answer a call, so offering one
  // would attach a flow that never runs.
  const agents = useQuery({
    queryKey: [...getSettingsQueryKeys().flowOptionsBase, 'sandbox-connect', AgentState.ACTIVE],
    queryFn: () => {
      let loaded = 0
      return collectPages(
        async (pageIndex) => {
          const payload = (
            await settingsService.listFlowOptions({
              status: AgentState.ACTIVE,
              page: pageIndex + 1,
              limit: CONNECT_PAGE_SIZE,
            })
          ).data
          const items = payload?.objects ?? []
          loaded += items.length
          // An empty page also ends the walk: without it a `total` that never
          // shrinks would spin to the cap.
          return { items, hasNext: items.length > 0 && hasNextFromTotal(loaded, payload?.total) }
        },
        (flow) => ({ id: flow.id, label: flow.name }),
      )
    },
    enabled: kind === SandboxConnectKind.agent,
  })

  const applications = useQuery({
    queryKey: [...getAPIConsoleQueryKeys().xmlApplications, 'sandbox-connect'],
    queryFn: () =>
      collectPages(
        async (pageIndex) => {
          const payload = (
            await settingsService.listApplications({
              limit: CONNECT_PAGE_SIZE,
              offset: pageIndex * CONNECT_PAGE_SIZE,
            })
          ).data
          return { items: payload?.objects ?? [], hasNext: hasNextFromMeta(payload?.meta) }
        },
        (app) => ({ id: app.app_id, label: app.app_name }),
      ),
    enabled: kind === SandboxConnectKind.stream,
  })

  // Inbound only: the dialog attaches a trunk that receives calls on this
  // number. An outbound trunk is not something a number routes to.
  const trunks = useQuery({
    queryKey: [...getAPIConsoleQueryKeys().inboundTrunks, 'sandbox-connect'],
    queryFn: () =>
      collectPages(
        async (pageIndex) => {
          const payload = (
            await settingsService.listInboundTrunksInternal({
              limit: String(CONNECT_PAGE_SIZE),
              offset: String(pageIndex * CONNECT_PAGE_SIZE),
            })
          ).data
          return { items: payload?.objects ?? [], hasNext: hasNextFromMeta(payload?.meta) }
        },
        (trunk) => ({ id: String(trunk.id), label: trunk.name }),
      ),
    enabled: kind === SandboxConnectKind.sip,
  })

  // Read only the enabled query: `isPending` stays true for a disabled one, so
  // reading them together would report every closed dialog as loading.
  const byKind = {
    [SandboxConnectKind.agent]: agents,
    [SandboxConnectKind.stream]: applications,
    [SandboxConnectKind.sip]: trunks,
  }
  const active = kind ? byKind[kind] : undefined

  if (!active) return { ...EMPTY, isPending: false, isError: false }

  return {
    options: active.data?.options ?? [],
    truncated: Boolean(active.data?.truncated),
    isPending: active.isPending,
    isError: active.isError,
  }
}
